import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import SupportChannels from './components/SupportChannels';
import FAQSection from './components/FAQSection';
import ContactForm from './components/ContactForm';
import KnowledgeBase from './components/KnowledgeBase';
import SupportStats from './components/SupportStats';
import CTASection from './components/CTASection';
import Footer from '../../components/Footer';

const SupportPage = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How does OpenbooQ's transparent pricing work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our pricing is completely transparent with no hidden markups. We charge one flat 20% Service & Warranty fee on top of actual material and labor costs — with no markup on either. You'll see exactly what you're paying for: materials at wholesale cost, labor at actual cost with no markup, our 20% Service & Warranty fee, and all permits and inspections. No surprises, no inflated quotes."
        }
      },
      {
        "@type": "Question",
        "name": "What makes OpenbooQ different from other roofing companies?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We've revolutionized the roofing industry through radical pricing transparency, technology-driven process with automated measurements, a certified installer network of vetted professionals, a 3-year comprehensive warranty, real-time project tracking, and no sales pressure or gimmicks."
        }
      },
      {
        "@type": "Question",
        "name": "How quickly can you start my roofing project?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Standard replacements typically start within 1-2 weeks, custom projects within 2-4 weeks, weather permitting. We'll provide an exact timeline during your consultation based on your specific needs, material availability, and installer schedule."
        }
      },
      {
        "@type": "Question",
        "name": "How do you measure my roof without visiting my property?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We use advanced satellite imagery and AI technology to create precise roof measurements with high-resolution satellite data, 3D modeling software, slope and angle calculations, and material quantity estimates with 99.5% accuracy guarantee."
        }
      },
      {
        "@type": "Question",
        "name": "What does your 3-year warranty cover?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our comprehensive warranty includes workmanship guarantee for 3 years and material defects coverage, plus manufacturer warranties on all materials (10-50 years depending on product)."
        }
      },
      {
        "@type": "Question",
        "name": "How do I file a warranty claim?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Filing a claim is simple: contact our support team (24/7), provide photos and description, schedule inspection within 48 hours, receive repair estimate, approve and schedule work, and receive quality assurance follow-up. Most claims are resolved within 5-7 business days."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://openbooq.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Support",
        "item": "https://openbooq.com/support"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Support Center - OpenbooQ Marketing Hub | 24/7 Roofing Help & Expert Guidance</title>
        <meta 
          name="description" 
          content="Need help? The OpenbooQ Support Center offers 24/7 roofing assistance and expert guidance to answer all your roof replacement and repair questions." 
        />
        <meta name="keywords" content="roofing support, 24/7 help, emergency roofing, live chat, phone support, roofing FAQ, expert guidance, customer service" />
        <meta property="og:title" content="Support Center - OpenbooQ Marketing Hub" />
        <meta property="og:description" content="24/7 expert roofing support with live chat, phone assistance, emergency services, and comprehensive knowledge base." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        <main>
          <HeroSection />
          <SupportChannels />
          <FAQSection />
          <KnowledgeBase />
          <SupportStats />
          <ContactForm />
          <CTASection />
        </main>
        
        {/* SEO Body Copy Section */}
        <section className="py-16 px-4 bg-gray-50 border-t border-gray-200">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Expert Roofing Support When You Need It</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Replacing your roof is a big investment. Questions can come up at any point — from your first roof price to the final inspection. Our support team knows roofing inside and out. We can help with shingle products, installation timelines, warranty coverage, and financing.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What We Help With</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> Reading your quote and pricing breakdown</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> Comparing shingles, warranties, and performance ratings</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> Financing options and payment plans</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> Scheduling and project timeline questions</li>
                  <li className="flex items-start gap-2"><span className="text-blue-500 mt-1">•</span> Warranty claims and maintenance tips after installation</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Emergency Roofing Help</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  Storm damage, leaks, and structural issues need fast action. Our emergency line connects you with certified contractors right away. They can assess the damage, add temporary weatherproofing, and fast-track your repair.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  If you have storm damage, take photos before any repairs start. This helps with insurance claims and gives our team the clearest picture of what needs to be done.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Support Throughout Your Entire Project</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Most roofing companies disappear after the sale. We don't. OpenbooQ stays with you from your first roof price to your final inspection. Our team is always available to make sure everything goes smoothly.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="flex items-start gap-3">
                  <span className="text-blue-500 text-xl font-bold">1.</span>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Before Your Project</p>
                    <p className="text-gray-500 text-sm">Get answers on pricing, materials, and timelines</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-blue-500 text-xl font-bold">2.</span>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">During Installation</p>
                    <p className="text-gray-500 text-sm">Track progress and ask questions in real time</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-blue-500 text-xl font-bold">3.</span>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">After Completion</p>
                    <p className="text-gray-500 text-sm">File warranty claims and get maintenance guidance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile bottom padding for sticky CTA */}
        <div className="h-20 lg:hidden"></div>

        <Footer />
      </div>
    </>
  );
};

export default SupportPage;